import { useEffect } from "react";

/**
 * 표 행 클릭 강조 — 전역 클릭 위임 한 곳으로 처리 (페이지별 state 없음).
 *
 * 대상: `table.data-table`, `table.tbl`, `table[data-row-hl]` 의 `tbody > tr`.
 * 제외: `table[data-no-row-hl]` (React state 로 이미 is-selected 를 관리하는 표 등).
 *
 * 동작
 *  - 행 클릭 → 같은 표의 이전 강조를 지우고 그 행에 `is-selected`.
 *  - 강조된 행 재클릭 → 해제. Esc → 전체 해제.
 *  - 행 안의 버튼·링크·입력 요소 클릭은 무시(원래 동작만).
 *  - 드래그로 텍스트를 선택한 직후의 클릭은 무시(복사 흐름 방해 안 함).
 *
 * 한계: 클래스를 DOM 에 직접 붙이므로 onSnapshot 갱신으로 행이 다시 마운트되면 강조가 풀릴 수 있습니다.
 * "따라 읽기용 강조" 목적이라 허용 범위로 봅니다.
 */
const TABLE_SEL = "table.data-table, table.tbl, table[data-row-hl]";
const INTERACTIVE_SEL = "button, a, input, select, textarea, label, summary, [role='button'], [contenteditable='true']";
const CLS = "is-selected";

function clearAll() {
  document.querySelectorAll<HTMLTableElement>(TABLE_SEL).forEach((table) => {
    if (table.hasAttribute("data-no-row-hl")) return;
    table.querySelectorAll<HTMLTableRowElement>(`:scope > tbody > tr.${CLS}`).forEach((tr) => tr.classList.remove(CLS));
  });
}

export function useRowHighlight() {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0) return;
      const target = e.target as Element | null;
      if (!target) return;
      if (target.closest(INTERACTIVE_SEL)) return;

      const tr = target.closest<HTMLTableRowElement>("tbody > tr");
      if (!tr) return;
      const table = tr.closest<HTMLTableElement>("table");
      if (!table || !table.matches(TABLE_SEL) || table.hasAttribute("data-no-row-hl")) return;
      // 중첩 표: tr 이 이 table 의 직계 tbody 소속일 때만
      if (tr.parentElement?.parentElement !== table) return;

      const sel = window.getSelection();
      if (sel && !sel.isCollapsed) return;

      if (tr.classList.contains(CLS)) {
        tr.classList.remove(CLS);
        return;
      }
      table.querySelectorAll<HTMLTableRowElement>(`:scope > tbody > tr.${CLS}`).forEach((r) => r.classList.remove(CLS));
      tr.classList.add(CLS);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") clearAll();
    };
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);
}
